import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { WebMercatorViewport } from '@deck.gl/core/typed';
import { useOnClickOutside, useUpdateBbox } from 'app/hooks';
import { MapSettings } from '../MapSettings';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { CheckBoxStateI, mapConfigStateT } from 'app/hooks/useGetMapSettingsState';
import { WeatherVisibility } from '../WeatherVisibility/WeatherVisibility';
import { AreasInMapSettings } from '../AreasInMapSettings';
import { setRulerTool } from 'app/utils/mapStateSlice';

import styles from './MapControlPanel.module.scss';

export type SettingState = [boolean, React.Dispatch<React.SetStateAction<boolean>>];

interface MapSettings {
  showVesselsState: SettingState;
  showPortsState: SettingState;
  showTerminalsState: SettingState;
  showAtonsState: SettingState;
  showSeaMarksState: SettingState;
  showLegendState: SettingState;
  showVesselsNamesState: SettingState;
  autoRefreshState: SettingState;
  showRouteState: SettingState;
  showRoutePredictedState: SettingState;
  showIndicatorState: SettingState;
  mapConfigState: [mapConfigStateT, React.Dispatch<React.SetStateAction<mapConfigStateT>>];
  checkBoxSpecialAreasState: CheckBoxStateI;
  showAnchorageState: SettingState;
}

interface MapControlPanelI {
  viewStateControl: [any, Dispatch<SetStateAction<any>>];
  mapSettingsStates: MapSettings;
  setShowBboxVessels: (value: SetStateAction<boolean>) => void;
  setShowFilter: (value: SetStateAction<boolean>) => void;
  disabledMode: boolean;
  isZoomEnabled: boolean;
}

export const MapControlPanel = ({
  viewStateControl,
  mapSettingsStates,
  setShowBboxVessels,
  setShowFilter,
  disabledMode,
  isZoomEnabled,
}: MapControlPanelI) => {
  const { t } = useTranslation();
  const updateBbox = useUpdateBbox();
  const dispatch = useAppDispatch();

  const uiconfig = useAppSelector((state) => state.userSlice.uiConfig);

  const [viewState, setViewState] = viewStateControl;
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showWeather, setShowWeather] = useState<boolean>(false);
  const [showSettingsAreas, setShowSettingsAreas] = useState<boolean>(false);

  const filters = useAppSelector((state) => state.mapStateSlice.filters);
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const rulerOn = useAppSelector((state) => state.mapStateSlice.tools.ruler);

  const isHistoryMode = mode === 'history';
  const isAnalyticsMode = mode === 'analytics';
  const isAreasMode = mode === 'areas';
  const isPlaybackMode = mode === 'playback';
  const isRouteBuilderMode = mode === 'routes';

  const handleZoom = (val: number) => {
    const newZoom = viewState.zoom + val > 1 ? viewState.zoom + val : 1;

    const viewport = new WebMercatorViewport({ ...viewState, width: viewState.width, height: viewState.height, zoom: newZoom });

    const bounds = viewport.getBounds();

    const longitude = bounds[2] - (bounds[2] - bounds[0]) / 2;
    const latitude = bounds[3] - (bounds[3] - bounds[1]) / 2;

    const borderViewport = viewport.fitBounds([
      [-180, 21],
      [180, 21],
    ]);

    localStorage.setItem('center', JSON.stringify([longitude, latitude]));
    localStorage.setItem('zoom', JSON.stringify(newZoom));
    if (isAnalyticsMode && viewport.zoom > borderViewport.zoom && !disabledMode) {
      setViewState((prev: any) => ({ ...prev, longitude, latitude, zoom: newZoom }));
      updateBbox(viewport);
      //необходимо пересчитать максимальное отдаление в режиме аналитики
    } else if (!isAnalyticsMode) {
      setViewState((prev: any) => ({ ...prev, longitude, latitude, zoom: newZoom }));
      updateBbox(viewport);
    }
  };

  const handleMinZoom = () => {
    if (!disabledMode || !isAnalyticsMode) {
      const viewport = new WebMercatorViewport({ width: viewState.width, height: viewState.height, zoom: 1 });
      localStorage.setItem('center', JSON.stringify([0, 0]));
      localStorage.setItem('zoom', JSON.stringify(1));
      setViewState((prev: any) => ({ ...prev, longitude: 0, latitude: 0, zoom: 1 }));
      updateBbox(viewport);
    }
  };

  const areasCtrl = useRef<HTMLDivElement>(null);
  const mapSettingsCtrl = useRef<HTMLDivElement>(null);
  const weatherSettingCrtl = useRef<HTMLDivElement>(null);

  useOnClickOutside(areasCtrl, () => {
    setShowSettingsAreas(false);
  });

  useOnClickOutside(mapSettingsCtrl, () => {
    setShowSettings(false);
  });

  useOnClickOutside(weatherSettingCrtl, () => {
    setShowWeather(false);
  });

  return (
    <>
      <div className={styles.controls_view_horizontal}>
        <div
          onClick={() => setShowFilter(true)}
          aria-disabled={isHistoryMode || isAnalyticsMode || isPlaybackMode}
          className={styles.icon_button}
          title={t('ShowFiltersTitle')}
        >
          {filters?.touched ? <Icon name="filter-map-active" /> : <Icon name="filter-map" />}
        </div>

        <div
          className={styles.icon_button}
          aria-disabled={viewState.zoom < 4 || isHistoryMode || isAnalyticsMode || isPlaybackMode}
          title={t('ShowBboxVesselsTitle')}
          onClick={() => {
            setShowBboxVessels((prev) => !prev);
          }}
        >
          <Icon name="vessels-list" />
        </div>

        <div
          className={styles.icon_button}
          title={t('Layers')}
          data-active={showSettingsAreas}
          aria-disabled={isAnalyticsMode || isPlaybackMode}
          ref={areasCtrl}
        >
          <Icon name="menu-layers" onClick={() => setShowSettingsAreas((prev) => !prev)} />
          {showSettingsAreas && <AreasInMapSettings checkBoxSpecialAreasState={mapSettingsStates.checkBoxSpecialAreasState} />}
        </div>

        <div
          className={styles.icon_button}
          data-active={showWeather}
          title={t('Weather')}
          aria-disabled={!uiconfig?.feWeatherAvailable || isHistoryMode || isPlaybackMode || isAnalyticsMode}
          ref={weatherSettingCrtl}
        >
          <Icon name="weather" onClick={() => setShowWeather((prev) => !prev)} />
          {showWeather && !showSettings && <WeatherVisibility />}
        </div>

        <div
          className={styles.icon_button}
          data-toggler={true}
          data-active={rulerOn}
          onClick={() => {
            dispatch(setRulerTool(!rulerOn));
          }}
          aria-disabled={!uiconfig?.feErblAvailable}
        >
          <Icon name="tool-ruler" />
        </div>

        <div className={styles.icon_button} title={t('ShowSettingTitle')} data-active={showSettings} ref={mapSettingsCtrl}>
          <Icon name="legend" onClick={() => setShowSettings((prev) => !prev)} />
          {showSettings && (
            <MapSettings
              isHistoryMode={isHistoryMode}
              showPorts={mapSettingsStates.showPortsState}
              showTerminals={mapSettingsStates.showTerminalsState}
              showRoutes={mapSettingsStates.showRouteState}
              showRoutePredicted={mapSettingsStates.showRoutePredictedState}
              showVessels={mapSettingsStates.showVesselsState}
              showSeaMarks={mapSettingsStates.showSeaMarksState}
              showLegend={mapSettingsStates.showLegendState}
              showVesselsNames={mapSettingsStates.showVesselsNamesState}
              showIndicator={mapSettingsStates.showIndicatorState}
              isAutorefrshAllowed={Boolean(uiconfig?.feMapAutoRefreshAvailable)}
              autoRefresh={mapSettingsStates.autoRefreshState}
              mapConfigState={mapSettingsStates.mapConfigState}
              showAtons={mapSettingsStates.showAtonsState}
              showAnchorageState={mapSettingsStates.showAnchorageState}
            />
          )}
        </div>
      </div>

      <div className={styles.controls_view}>
        <div
          className={styles.icon_button}
          aria-disabled={!isZoomEnabled}
          onClick={() => {
            handleZoom(1);
          }}
        >
          <Icon name="plus" />
        </div>

        <div
          className={styles.icon_button}
          aria-disabled={!isZoomEnabled}
          onClick={() => {
            handleZoom(-1);
          }}
        >
          <Icon name="minus" />
        </div>

        <div className={styles.icon_button} aria-disabled={!isZoomEnabled} title={t('MinZoomTitle')} onClick={handleMinZoom}>
          <Icon name="zoom-minimum" />
        </div>
      </div>
    </>
  );
};
